// App-level configuration.
//
// Three layers (per the Configuration Management spec):
//   1. BASELINE_CONFIG — the guaranteed in-repo fallback (this file). Always valid.
//   2. Build artifact — scripts/build-config.mjs merges content/app-config.json over the env
//      baseline and writes public/app-config.json (copied into dist/ by Vite).
//   3. Runtime — loadConfig() fetches /app-config.json at boot and layers it over BASELINE_CONFIG;
//      any failure falls back to the baseline. useAppConfig() re-renders consumers on change.
//
// Admin edits (draft/publish to DynamoDB) layer in via the content overlay in a later increment;
// for now the active config is whatever build-config.mjs emitted.
import { useSyncExternalStore } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type FooterTitleFormat = "name-app" | "name-possessive-app" | "app";

export interface AppConfig {
  appTitle: string;
  // Master interactivity switch. When false the site serves only the flat Print/PDF résumé + a
  // "bring your own AI" prompt — no API / data / AI calls at all. Read from the baked config at boot
  // (before any network call), so a disabled site never even reaches the backend. Default true.
  interactive: boolean;
  appearance: { mode: ThemeMode; themeColor: string | null };
  header: { caseStudy: boolean; designSystem: boolean };
  // Point-and-click bug reports: the element picker is always available to the signed-in admin;
  // `bugPickerPublic` opens it to every visitor (runtime-tunable via the config overlay).
  feedback: { bugPickerPublic: boolean };
  footer: {
    copyrightYear: number | "auto";
    titleFormat: FooterTitleFormat;
    disclaimerText: string;
    show: {
      aiReadable: boolean;
      linkedin: boolean;
      facebook: boolean;
      instagram: boolean;
      contactEmail: boolean;
      contactPhone: boolean;
    };
  };
  profile: {
    firstName: string;
    lastName: string;
    contactEmail: string;
    contactPhone: string;
    social: { linkedin: string; facebook: string; instagram: string };
  };
  // Ask-AI quota. freeLimit/emailGrant here are DISPLAY ONLY — the server (quota-config overlay) is
  // the enforced source of truth; copy strings drive the exhausted-state next-best-actions verbiage.
  askAi: {
    freeLimit: number;
    emailGrant: number;
    copy: {
      exhaustedTitle: string;
      exhaustedSub: string;
      shareEmail: string;
      emailSrinath: string;
      askOwnAi: string;
      feedback: string;
    };
  };
  // pages are a placeholder stub, added in a later increment.
}

const DISCLAIMER_DEFAULT =
  "This is a demo site and may contain partial information about engagements, which are described in " +
  "anonymized terms by design. It blends my own writing with AI-assisted drafts, all based on my real " +
  "work and reviewed by me before publishing. If you share your email for more AI questions, it's used " +
  "only to send your access link and is stored as a one-way keyed hash, never in plain text. Views are " +
  "my own, not my employer's. Informational only, not a warranty or an offer.";

export const BASELINE_CONFIG: AppConfig = {
  appTitle: (import.meta.env.VITE_APP_TITLE ?? "").trim() || "Interactive Resume",
  interactive: true,
  appearance: { mode: "system", themeColor: null },
  header: { caseStudy: true, designSystem: true },
  feedback: { bugPickerPublic: false },
  footer: {
    copyrightYear: "auto",
    titleFormat: "name-app",
    disclaimerText: DISCLAIMER_DEFAULT,
    show: {
      aiReadable: true,
      linkedin: true,
      facebook: false,
      instagram: false,
      contactEmail: true,
      contactPhone: false,
    },
  },
  profile: {
    firstName: "",
    lastName: "",
    contactEmail: "",
    contactPhone: "",
    social: { linkedin: "", facebook: "", instagram: "" },
  },
  askAi: {
    freeLimit: 3,
    emailGrant: 3,
    copy: {
      exhaustedTitle: "You've used your free questions",
      exhaustedSub: "Keep going — get more, ask your own AI, or reach me directly:",
      shareEmail: "Email me a link",
      emailSrinath: "Email your question",
      askOwnAi: "Ask your own AI",
      feedback: "Send feedback",
    },
  },
};

// Back-compat: existing consumers import these directly. They reflect the in-repo baseline; the
// footer/disclaimer move to the resolved runtime config when App Settings (C3) ships.
export const APP_TITLE: string = BASELINE_CONFIG.appTitle;
export const DISCLAIMER_TEXT: string = BASELINE_CONFIG.footer.disclaimerText;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// Deep-merge an override over a base of the same shape; unknown/extra keys on the override are
// ignored, and any non-object override replaces the base value.
export function deepMerge<T>(base: T, over: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(over)) {
    return (over === undefined ? base : (over as T));
  }
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(base)) {
    if (key in over) out[key] = deepMerge((base as Record<string, unknown>)[key], over[key]);
  }
  return out as T;
}

let resolved: AppConfig = BASELINE_CONFIG;
const listeners = new Set<() => void>();

/** Fetch the build-time runtime config and layer it over the baseline. Best-effort; never throws. */
export async function loadConfig(): Promise<void> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch("/app-config.json", { cache: "no-store", signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return;
    const data = (await res.json()) as unknown;
    resolved = deepMerge(BASELINE_CONFIG, data);
    for (const l of listeners) l();
  } catch {
    resolved = BASELINE_CONFIG; // explicit: fall back to the guaranteed baseline
  }
}

/** Apply an admin-published config to the running app immediately (Publish path). */
export function applyConfig(cfg: AppConfig): void {
  resolved = deepMerge(BASELINE_CONFIG, cfg);
  for (const l of listeners) l();
}

/** Synchronous current config (baseline until loadConfig() resolves). */
export function getAppConfig(): AppConfig {
  return resolved;
}

/** Reactive config for components; re-renders when loadConfig() lands. */
export function useAppConfig(): AppConfig {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => resolved,
    () => resolved,
  );
}

/** The resolved appearance (default mode + theme color). Read by the theme resolver in settings.ts. */
export function getAppearance(): AppConfig["appearance"] {
  return resolved.appearance;
}

/** Subscribe to config changes (e.g. an admin publish); returns an unsubscribe. */
export function subscribeConfig(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
